import { useState } from "react";
import { useApp } from "../../store.jsx";
import { Icon } from "../Common.jsx";
import MemberPicker from "../common/MemberPicker.jsx";
import { createProject, updateProject, setProjectMembers } from "../../lib/api";

const toDateInput = (d) => (d ? new Date(d).toISOString().slice(0, 10) : "");

const ERR_MAP = {
  deadline_before_start: "Deadline can't be before the start date.",
  invalid_input: "Please check the form and try again.",
  project_archived: "This project is archived and can't be edited.",
  user_not_found: "One of the selected members no longer exists.",
};

export default function ProjectFormModal({ project, onSaved }) {
  const { closeModal, toast } = useApp();
  const editing = !!project;
  const [name, setName] = useState(project?.name || "");
  const [description, setDescription] = useState(project?.description || "");
  const [startDate, setStartDate] = useState(toDateInput(project?.startDate));
  const [deadline, setDeadline] = useState(toDateInput(project?.deadline));
  const [selected, setSelected] = useState(
    () => new Set((project?.members || []).map((m) => m.userId)),
  );
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const submit = async () => {
    setErr("");
    if (!name.trim()) {
      setErr("Project name is required.");
      return;
    }
    if (startDate && deadline && deadline < startDate) {
      setErr("Deadline can't be before the start date.");
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await updateProject(project.id, {
          name: name.trim(),
          description: description.trim() ? description.trim() : null,
          startDate: startDate || null,
          deadline: deadline || null,
        });
        await setProjectMembers(project.id, [...selected]);
        toast("Project updated", "success");
      } else {
        await createProject({
          name: name.trim(),
          description: description.trim() || undefined,
          startDate: startDate || undefined,
          deadline: deadline || undefined,
          memberIds: [...selected],
        });
        toast("Project created", "success");
      }
      closeModal();
      onSaved?.();
    } catch (e) {
      setErr(ERR_MAP[e.message] || e.message || "Could not save project.");
      setSaving(false);
    }
  };

  return (
    <>
      <div className="modal-head">
        <h3>{editing ? "Edit project" : "New project"}</h3>
        <button className="icon-btn" onClick={closeModal}>
          <Icon name="x" size={16} />
        </button>
      </div>
      <div className="modal-body" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {err && (
          <div className="badge badge-error" style={{ padding: "8px 12px", whiteSpace: "normal" }}>
            {err}
          </div>
        )}
        <div className="field">
          <label>Name *</label>
          <input
            className="input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Website Redesign"
            maxLength={160}
          />
        </div>
        <div className="field">
          <label>Description</label>
          <textarea
            className="input"
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Optional"
            maxLength={5000}
          />
        </div>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          <div className="field" style={{ flex: 1, minWidth: 140 }}>
            <label>Start date</label>
            <input
              type="date"
              className="input"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>
          <div className="field" style={{ flex: 1, minWidth: 140 }}>
            <label>Deadline</label>
            <input
              type="date"
              className="input"
              value={deadline}
              onChange={(e) => setDeadline(e.target.value)}
            />
          </div>
        </div>
        <div className="field">
          <label>Members ({selected.size})</label>
          <MemberPicker selected={selected} onChange={setSelected} />
        </div>
      </div>
      <div className="modal-foot">
        <button className="btn btn-ghost" onClick={closeModal} disabled={saving}>
          Cancel
        </button>
        <button className="btn btn-primary" onClick={submit} disabled={saving}>
          {saving ? "Saving…" : editing ? "Save changes" : "Create project"}
        </button>
      </div>
    </>
  );
}
